'use client'

import { Button, Cell, Dialog, Navigation } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { navigateToPage } from '@marketplace-web/shared/browser'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { GO_TO_WALLET_URL } from 'constants/routes'
import {
  ConversationEducationModel,
  EducationAction,
  EducationType,
  markEducationAsRead,
} from '@marketplace-web/conversation-education/conversation-education-data'

import ConversationEducationContent from '../ConversationEducationContent'

type Props = {
  conversation: ConversationEducationModel
  isOpen: boolean
  onClose: () => void
}

const SUPPORTED_TYPES = [
  EducationType.InConversationSold,
  EducationType.InConversationTransactionCompleted,
  EducationType.InConversationSoldHAOV,
  EducationType.InConversationTransactionCompletedHAOV,
  EducationType.InConversationSoldElectronics,
  EducationType.InConversationSoldElectronicsEvs,
  EducationType.InConversationTransactionCompletedElectronics,
  EducationType.InConversationTransactionCompletedElectronicsEvs,
]

const ConversationEducationModal = ({ conversation, isOpen, onClose }: Props) => {
  const translate = useTranslate('conversation.education.modal')

  if (!conversation?.education) return null

  const {
    education: { type, action },
  } = conversation

  const closeModal = async () => {
    await markEducationAsRead(type)

    onClose()
  }

  const handleGoToWalletActionClick = async () => {
    await markEducationAsRead(type)

    navigateToPage(GO_TO_WALLET_URL)
  }

  const renderNavigation = () => (
    <div className="conversation-education__background">
      <Navigation
        theme="transparent"
        right={
          <Button
            aria={{
              'aria-label': translate('a11y.actions.close'),
            }}
            styling={Button.Styling.Flat}
            theme="amplified"
            iconName={X24}
            inline
            onClick={closeModal}
            testId="conversation-education-modal-close-button"
          />
        }
      />
    </div>
  )

  const renderContent = () => (
    <ScrollableArea>
      <ConversationEducationContent conversation={conversation} type={type} />
    </ScrollableArea>
  )

  const renderActionButton = () => {
    if (action === EducationAction.Read) {
      return (
        <Cell theme="transparent">
          <Button
            styling={Button.Styling.Filled}
            onClick={closeModal}
            text={translate('actions.go_to_conversation')}
            testId="conversation-education-modal-go-to-conversation-button"
          />
        </Cell>
      )
    }

    if (action === EducationAction.GoToWallet) {
      return (
        <Cell theme="transparent">
          <Button
            styling={Button.Styling.Filled}
            onClick={handleGoToWalletActionClick}
            text={translate('actions.got_to_wallet')}
            testId="conversation-education-modal-go-to-wallet-button"
          />
        </Cell>
      )
    }

    return null
  }

  if (!SUPPORTED_TYPES.includes(type)) return null

  return (
    <Dialog show={isOpen} hasScrollableContent>
      <div className="u-fill-width">
        {renderNavigation()}
        {renderContent()}
        {renderActionButton()}
      </div>
    </Dialog>
  )
}

export default ConversationEducationModal
